// DRAWOLA:CUSTOM fork-only file. Auto-detect bullet/numbered lists and
// continue them on Enter inside the text WYSIWYG editor.
// See DRAWOLA.md for the full feature description and merge notes.

import { LIST_TYPE } from "@excalidraw/common";

import type { ExcalidrawElement } from "@excalidraw/element/types";
import type { ExcalidrawTextElement } from "@excalidraw/element/types";

import type App from "../components/App";

type ListTypeValue = typeof LIST_TYPE[keyof typeof LIST_TYPE];

/**
 * Detect list type from the first line of the textarea and sync it onto the
 * scene element. Also rewrites ". " or "- " shorthand into "• " at the start
 * of the value. Called from the WYSIWYG input handler after onChange.
 */
export const applyListAutoDetect = (
  editable: HTMLTextAreaElement,
  app: App,
  id: ExcalidrawElement["id"],
  onChange: (nextOriginalText: string) => void,
): void => {
  const currentEl = app.scene.getElement<ExcalidrawTextElement>(id);
  if (!currentEl) {
    return;
  }

  const firstLine = editable.value.split("\n")[0];
  const currentListType = currentEl.listType || LIST_TYPE.NONE;
  let detectedType: ListTypeValue = LIST_TYPE.NONE;

  if (/^\d+\.\s/.test(firstLine)) {
    detectedType = LIST_TYPE.NUMBERED;
  } else if (
    firstLine.startsWith("• ") ||
    firstLine.startsWith(". ") ||
    firstLine.startsWith("- ")
  ) {
    detectedType = LIST_TYPE.BULLET;
  }

  if (detectedType === currentListType) {
    return;
  }

  // replace ". " or "- " shorthand with "• " when bullet is detected
  if (
    detectedType === LIST_TYPE.BULLET &&
    (editable.value.startsWith(". ") || editable.value.startsWith("- "))
  ) {
    const { selectionStart, selectionEnd } = editable;
    editable.value = `• ${editable.value.slice(2)}`;
    editable.selectionStart = selectionStart;
    editable.selectionEnd = selectionEnd;
  }

  app.scene.mutateElement(currentEl, { listType: detectedType });
  onChange(editable.value);
};

/**
 * Handle Enter inside an active list: insert the next bullet / number prefix,
 * or end the list when the current line is an empty prefix. Returns true when
 * the keypress was consumed so the caller can skip default behavior.
 */
export const handleListEnterKey = (
  editable: HTMLTextAreaElement,
  app: App,
  id: ExcalidrawElement["id"],
  event: KeyboardEvent,
): boolean => {
  const updatedEl = app.scene.getElement<ExcalidrawTextElement>(id);
  const listType = updatedEl?.listType;

  if (!listType || listType === LIST_TYPE.NONE) {
    return false;
  }

  event.preventDefault();

  const { selectionStart } = editable;
  const before = editable.value.slice(0, selectionStart);
  const after = editable.value.slice(editable.selectionEnd);

  // count existing lines before cursor to determine next number
  const linesBefore = before.split("\n");
  const prefix =
    listType === LIST_TYPE.BULLET
      ? "• "
      : `${linesBefore.length + 1}. `;

  // if current line is only a prefix with no content, remove it and end list
  const currentLine = linesBefore[linesBefore.length - 1];
  const isEmptyListLine = /^(• |\d+\. )$/.test(currentLine);

  if (isEmptyListLine) {
    const newBefore = linesBefore.slice(0, -1).join("\n");
    editable.value = newBefore + (newBefore ? "\n" : "") + after;
    editable.selectionStart = newBefore.length + (newBefore ? 1 : 0);
    editable.selectionEnd = editable.selectionStart;
  } else {
    editable.value = `${before}\n${prefix}${after}`;
    editable.selectionStart = selectionStart + 1 + prefix.length;
    editable.selectionEnd = editable.selectionStart;
  }

  editable.dispatchEvent(new Event("input"));
  return true;
};
